import { useRef } from "react";
import { FaPlus } from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { updateViewState } from "../../../../store/Slices/ViewManagerSlice";
import { updateAddStatus } from "../../../../store/Slices/AddStatusSlice";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import toast from "react-hot-toast";

interface MyStatusProps {
  time: string;
  name: string;
  classes?: string;
  currentStatusId: number;
}

export default function MyStatus({
  name,
  time,
  classes,
  currentStatusId,
}: MyStatusProps) {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const statusData = useAppSelector((state) => state.status);
  const userData = useAppSelector((state) => state.userData);

  function handleStatusClick() {
    if (statusData.data.my_stories.length == 0) {
      return;
    }
    dispatch(
      updateViewState({
        showMyStatusModal: true,
        currentStatusId: currentStatusId,
      }),
    );
  }

  function handleFileSelectClick(e: React.MouseEvent) {
    e.stopPropagation();
    fileInputRef.current?.click();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const filesArray = Array.from(selectedFiles);
      const imageFiles = filesArray.filter((file) =>
        file.type.startsWith("image/"),
      );

      if (imageFiles.length === 0) {
        toast.error("Only image files are allowed for status.");
        return;
      }

      dispatch(updateAddStatus({ status_text: "", files: imageFiles }));
      dispatch(
        updateViewState({
          showAddStatusModal: true,
          currentStatusId: currentStatusId,
        }),
      );
    }
  }

  const hasStories = statusData.data.my_stories.length > 0;

  return (
    <div className="flex items-center gap-3 px-3 py-3">
      <button
        type="button"
        onClick={handleStatusClick}
        className="relative shrink-0"
        aria-label="My status"
      >
        <img
          src={userData.profile_pic}
          className={`h-14 w-14 rounded-full object-cover ${classes ?? ""} ${
            hasStories
              ? "ring-[2.5px] ring-[var(--theme-color)] ring-offset-2 ring-offset-primary"
              : "ring-1 ring-black/5 dark:ring-white/10"
          }`}
          alt=""
        />
      </button>

      <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
        <button
          type="button"
          onClick={handleStatusClick}
          className="min-w-0 flex-1 text-left"
        >
          <div className="truncate text-[16px] font-semibold capitalize text-darkText">
            {name}
          </div>
          <div className="mt-0.5 truncate text-[13px] text-lightText">
            {time}
          </div>
        </button>

        <div className="flex shrink-0 items-center gap-2">
          {hasStories && (
            <button
              type="button"
              aria-label="View status"
              onClick={handleStatusClick}
              className="grid h-8 w-8 place-content-center rounded-full bg-black/[0.06] text-darkText transition active:scale-95 dark:bg-white/10"
            >
              <MdOutlineRemoveRedEye className="text-lg" />
            </button>
          )}
          <button
            type="button"
            aria-label="Add status"
            onClick={handleFileSelectClick}
            className="grid h-8 w-8 place-content-center rounded-full bg-themeColor text-white transition active:scale-95"
          >
            <FaPlus className="text-sm" />
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
    </div>
  );
}
